import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import {
  ActionSheetController,
  IonButton,
  IonIcon,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ScreensizeService } from '@okcargo-apps/layout-ionic';
import { ClickStopPropagationDirective } from '@okcargo-apps/ng-utils';
import { EStopType, IFreightBasicForCarrier, IFreightBasicForShipper } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import { AccountService } from '../../account/account.service';
import { FreightDocumentationService } from '../freight-documentation.service';
import { QrModalComponent } from '../qr-modal/qr-modal.component';

@Component({
  selector: 'lib-freight-documentation-links',
  templateUrl: './freight-documentation-links.component.html',
  styleUrls: ['./freight-documentation-links.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IonIcon, IonButton, ClickStopPropagationDirective, TranslateModule],
})
export class FreightDocumentationLinksComponent {
  private readonly freightDocumentationService = inject(FreightDocumentationService);
  private readonly accountService = inject(AccountService);
  private readonly screensizeService = inject(ScreensizeService);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly actionSheetCtrl = inject(ActionSheetController);
  private readonly modalCtrl = inject(ModalController);
  private readonly translate = inject(TranslateService);
  private readonly connectivity = inject(ConnectivityService);

  /** Downloading a document queries the backend: it cannot be done without connection. */
  protected readonly isOffline = this.connectivity.isOffline;

  // TODO: in the future, maybe refactor this component and use diferent services in diferent components for carrier and shipper
  public freight = input.required<IFreightBasicForCarrier | IFreightBasicForShipper>();

  protected buttonColor = computed(() => {
    const freight = this.freight();
    switch (freight.nextStopType) {
      case EStopType.LOAD:
        return 'success';
      case EStopType.UNLOAD_LOAD:
        return 'tertiary';
      default:
        return 'primary';
    }
  });

  protected readonly showQrButton = computed(() => {
    const freight = this.freight();

    const isMobile = this.screensizeService.isMobile();
    const isQrAvailable = 'dcdId' in freight && freight.qrSvg !== undefined && freight.qrSvg !== null;

    return isMobile && isQrAvailable;
  });

  // constructor() {}

  protected async openLoadingOrder(freight: IFreightBasicForCarrier | IFreightBasicForShipper): Promise<void> {
    if (this.isOffline()) return;

    const viewPrices = this.accountService.hasCapability(ECapabilities.VIEW_PRICES);

    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.downloading_file'),
    });
    loading.present();
    await this.freightDocumentationService.downloadLoadingOrder(freight, viewPrices);
    await loading.dismiss();
  }

  private async openECmr(freight: IFreightBasicForCarrier | IFreightBasicForShipper): Promise<void> {
    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.downloading_file'),
    });
    loading.present();
    await this.freightDocumentationService.downloadEcmr(freight);
    await loading.dismiss();
  }

  private async openDigitalControlDoc(freight: IFreightBasicForCarrier): Promise<void> {
    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.downloading_file'),
    });
    loading.present();
    await this.freightDocumentationService.downloadDigitalControlDoc(freight);
    await loading.dismiss();
  }

  protected async showDigitalControlDocQr(): Promise<void> {
    const freight = this.freight();

    if ('dcdId' in freight && freight.qrSvg !== undefined && freight.qrSvg !== null) {
      const modal = await this.modalCtrl.create({
        component: QrModalComponent,
        componentProps: {
          freight: this.freight(),
        },
      });
      modal.present();
    }
  }

  // TO-DO @oalvarez check isOpen or use actionSheetController (actionSheetController)?
  protected async viewDocs(): Promise<void> {
    if (this.isOffline()) return;

    const freight = this.freight();

    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Descarga de documentos',
      buttons: [
        {
          icon: '../assets/material-symbols/content_paste_24dp.svg',
          text: this.translate.instant('files.loading_order'),
          data: {
            action: 'openLoadingOrder',
          },
          handler: async () => {
            await actionSheet.dismiss();
            this.openLoadingOrder(freight);
          },
        },
        {
          icon: '../assets/material-symbols/description_24dp.svg',
          text: this.translate.instant('files.ecmr'),
          data: {
            action: 'openECmr',
          },
          handler: async () => {
            await actionSheet.dismiss();
            this.openECmr(freight);
          },
        },

        // TODO refactor this or use different component for carrier and shipper, because this feature is only for carrier
        ...('dcdId' in freight && freight.dcdId !== undefined && freight.dcdId !== null
          ? [
              {
                icon: '../assets/material-symbols/okc_doc_with_qr_24dp.svg',
                text: 'PDF Documento de Control',
                data: {
                  action: 'openDigitalControlDoc',
                },
                handler: async () => {
                  await actionSheet.dismiss();
                  this.openDigitalControlDoc(freight);
                },
              },
            ]
          : []),

        ...('dcdId' in freight && freight.qrSvg !== undefined && freight.qrSvg !== null
          ? [
              {
                icon: '../assets/material-symbols/qr_code_24dp.svg',
                text: 'QR Documento de Control',
                data: {
                  action: 'showDigitalControlDocQr',
                },
                handler: async () => {
                  await actionSheet.dismiss();
                  this.showDigitalControlDocQr();
                },
              },
            ]
          : []),

        {
          text: this.translate.instant('files.cancel'),
          role: 'cancel',
        },
      ],
    });

    await actionSheet.present();
  }
}
